import { WorkingPatternDayInput, WorkingPatternPeriods } from '../services/gql/graphql.generated';
import { secondsSinceMidnightToTimeString, timeStringToSecondsSinceMidnight } from './dateHelper';
import { dayIndexes } from './dayIndexes';

export interface EmployeeSchedule {
  dayNumber?: number;
  slotNumber?: number;
  id: string;
  day: string;
  start?: string;
  end?: string;
  available?: boolean;
}

const dayLabels: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday'
};

export const patternToEmployeeSchedule = (data: WorkingPatternPeriods) => {
  let returnVal: EmployeeSchedule[] = dayIndexes.map(index => ({
    dayNumber: index,
    slotNumber: 0,
    id: '',
    day: dayLabels[index],
    start: undefined,
    end: undefined,
    available: false
  }));
  const periods = data.periods;

  if (Array.isArray(periods) && periods.length > 0) {
    const pattern = periods[0].patterns ?? [];

    returnVal = dayIndexes.flatMap<EmployeeSchedule>(index => {
      const patternsForDay = pattern
        .filter(p => p.dayNumber === index)
        .sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));

      if (patternsForDay.length === 0) {
        return returnVal.find(day => day.dayNumber === index)!;
      }

      return patternsForDay.map(day => {
        const available = !!day.endTime;

        return {
          dayNumber: day.dayNumber,
          slotNumber: day.slotNumber ?? 0,
          id: day.id ?? '',
          day: day.day || dayLabels[index],
          start: available ? secondsSinceMidnightToTimeString(day.startTime) : undefined,
          end: available ? secondsSinceMidnightToTimeString(day.endTime) : undefined,
          available
        };
      });
    });
  }

  return returnVal;
};

export const employeeScheduleToPattern = (schedule: EmployeeSchedule[]) => {
  const patterns: WorkingPatternDayInput[] = [];
  const sortedSchedule = [...schedule].sort((left, right) => {
    if ((left.dayNumber ?? 0) !== (right.dayNumber ?? 0)) {
      return (left.dayNumber ?? 0) - (right.dayNumber ?? 0);
    }

    return (left.slotNumber ?? 0) - (right.slotNumber ?? 0);
  });

  sortedSchedule.forEach(day => {
    const startTime = day.available ? timeStringToSecondsSinceMidnight(day.start || '') : undefined;
    let endTime = day.available ? timeStringToSecondsSinceMidnight(day.end || '') : undefined;
    if (endTime === 0) {
      // An end time of 0 actually means 'a full day since midnight'.
      endTime = 24 * 60 * 60;
    }

    patterns.push({
      startTime: startTime,
      endTime: endTime,
      dayNumber: day.dayNumber,
      slotNumber: day.slotNumber ?? 0
    });
  });

  return patterns;
};
